import { useState } from "react";
import {
  applyVehicleModelCopyImport,
  validateVehicleModelCopyImport,
} from "../../../../services/vehicleModelCopyAdminApi";

const OUTCOME_FILTERS = ["", "CREATE_DRAFT", "UPDATE_DRAFT", "CONFLICT", "INVALID", "UNCHANGED"];

const VehicleModelCopyImportPanel = ({ canApply, filters = {} }) => {
  const [file, setFile] = useState(null);
  const [validation, setValidation] = useState(null);
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");

  const handleValidate = async () => {
    if (!file) {
      setErrorMessage("Choose a CSV or JSON file first.");
      return;
    }
    try {
      setLoading(true);
      setErrorMessage("");
      const result = await validateVehicleModelCopyImport(file);
      setValidation(result);
      setToast("Dry-run validation complete.");
    } catch (error) {
      setValidation(null);
      setErrorMessage(error.message || "Validation failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!canApply || !validation?.run?.id || !validation?.fileHash) return;
    const proceed = window.confirm(
      "Apply this import plan? Draft-only rows will be created or updated. Publishing remains a separate action."
    );
    if (!proceed) return;
    try {
      setApplying(true);
      setErrorMessage("");
      const result = await applyVehicleModelCopyImport({
        runId: validation.run.id,
        fileHash: validation.fileHash,
      });
      setToast(
        `Import applied: ${result.summary?.applied || 0} updated, ${result.summary?.unchanged || 0} unchanged, ${result.summary?.failed || 0} failed.`
      );
    } catch (error) {
      setErrorMessage(error.message || "Import apply failed.");
    } finally {
      setApplying(false);
    }
  };

  const filteredRows =
    validation?.rows?.filter((row) => !outcomeFilter || row.outcome === outcomeFilter) || [];

  return (
    <section className="admin-vehicle-model-copy-import panel-card" aria-label="Bulk import">
      <header>
        <h2>Bulk import</h2>
        <p className="muted">
          Upload CSV or JSON, review dry-run results, then apply approved draft changes.
        </p>
      </header>

      {toast ? (
        <p className="admin-dashboard__toast" role="status">
          {toast}
        </p>
      ) : null}
      {errorMessage ? (
        <p className="admin-state-panel admin-state-panel--error" role="alert">
          {errorMessage}
        </p>
      ) : null}

      <div className="admin-vehicle-model-copy-import__controls">
        <input
          type="file"
          accept=".csv,.json,application/json,text/csv"
          onChange={(event) => {
            setFile(event.target.files?.[0] || null);
            setValidation(null);
          }}
        />
        <button
          type="button"
          className="admin-dashboard__action-btn"
          disabled={loading || !file}
          onClick={handleValidate}
        >
          {loading ? "Validating…" : "Validate (dry run)"}
        </button>
        {canApply && validation?.run?.id ? (
          <button
            type="button"
            className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
            disabled={applying}
            onClick={handleApply}
          >
            {applying ? "Applying…" : "Apply import"}
          </button>
        ) : null}
      </div>

      {validation?.summary ? (
        <>
          <p>
            Creates: {validation.summary.createDraft || 0} · Updates:{" "}
            {validation.summary.updateDraft || 0} · Unchanged: {validation.summary.unchanged || 0} ·
            Conflicts: {validation.summary.conflicts || 0} · Invalid: {validation.summary.invalid || 0}
          </p>
          <label>
            Filter rows
            <select value={outcomeFilter} onChange={(event) => setOutcomeFilter(event.target.value)}>
              {OUTCOME_FILTERS.map((value) => (
                <option key={value || "all"} value={value}>
                  {value || "all"}
                </option>
              ))}
            </select>
          </label>
          <div className="admin-vehicle-model-copy-import__rows">
            <table>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Make / Model</th>
                  <th>Locale</th>
                  <th>Outcome</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.slice(0, 200).map((row) => (
                  <tr key={`${row.rowNumber}-${row.locale}`}>
                    <td>{row.rowNumber}</td>
                    <td>
                      {row.makeKey}/{row.modelKey}
                    </td>
                    <td>{row.locale}</td>
                    <td>{row.outcome}</td>
                    <td>{row.reasonCode || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      <p className="muted">
        Active filters: {filters.makeKey || "any make"} / {filters.modelKey || "any model"} /{" "}
        {filters.locale || "any locale"}
      </p>
    </section>
  );
};

export default VehicleModelCopyImportPanel;
